import { useState } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import GlassCard from '@/components/base/GlassCard';
import KpiCard from '@/components/base/KpiCard';
import { activeExperts, performanceTrend } from '@/mocks/phase2Data';

const statusStyle: Record<string, { bg: string; color: string; dot: string }> = {
  online: { bg: 'rgba(16,185,129,0.15)', color: '#10b981', dot: '#10b981' },
  meşgul: { bg: 'rgba(245,158,11,0.15)', color: '#f59e0b', dot: '#f59e0b' },
  offline: { bg: 'rgba(107,114,128,0.15)', color: '#6b7280', dot: '#4b5563' },
};

const ExpertsPage: React.FC = () => {
  const [filter, setFilter] = useState<'tümü' | 'online' | 'meşgul' | 'offline'>('tümü');
  const [search, setSearch] = useState('');

  const filtered = activeExperts.filter((e) => {
    const matchStatus = filter === 'tümü' || e.durum === filter;
    const matchSearch = e.isim.toLowerCase().includes(search.toLowerCase()) || e.sehir.toLowerCase().includes(search.toLowerCase());
    return matchStatus && matchSearch;
  });

  const onlineCount = activeExperts.filter((e) => e.durum === 'online').length;
  const busyCount = activeExperts.filter((e) => e.durum === 'meşgul').length;
  const offlineCount = activeExperts.filter((e) => e.durum === 'offline').length;
  const totalEarnings = activeExperts.reduce((s, e) => s + e.gunlukKazanc, 0);

  return (
    <div>
      <div className="mb-5">
        <h1 className="text-xl font-bold text-white" style={{ fontFamily: 'Inter, sans-serif' }}>Aktif Uzmanlar</h1>
        <p className="text-xs mt-0.5" style={{ color: '#6b7280' }}>Gerçek zamanlı uzman durumu ve performans</p>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
        <KpiCard label="Online Uzman" value={onlineCount} change={8.2} icon="ri-wifi-line" />
        <KpiCard label="Meşgul" value={busyCount} icon="ri-time-line" />
        <KpiCard label="Bugünkü Kazanç" value={totalEarnings.toLocaleString('tr-TR')} change={12.4} icon="ri-money-dollar-circle-line" suffix=" ₺" />
        <KpiCard label="Toplam Uzman" value={activeExperts.length} change={5.1} icon="ri-team-line" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
        {/* Expert list */}
        <div className="lg:col-span-2">
          <div className="rounded-xl overflow-hidden" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)' }}>
            {/* Filters */}
            <div className="flex flex-wrap items-center gap-2 p-4 border-b" style={{ borderColor: 'rgba(255,255,255,0.06)' }}>
              <div className="relative">
                <input
                  type="text"
                  placeholder="İsim veya şehir ara..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-7 pr-3 py-1.5 rounded-lg text-sm text-white outline-none w-44"
                  style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', fontSize: '13px' }}
                />
                <i className="ri-search-line absolute left-2 top-1/2 -translate-y-1/2 text-xs" style={{ color: '#6b7280' }}></i>
              </div>
              <div className="flex gap-1">
                {(['tümü', 'online', 'meşgul', 'offline'] as const).map((s) => (
                  <button
                    key={s}
                    onClick={() => setFilter(s)}
                    className="px-3 py-1 rounded-lg text-xs font-medium cursor-pointer transition-all whitespace-nowrap capitalize"
                    style={{
                      background: filter === s ? 'linear-gradient(135deg, rgba(255,77,77,0.25), rgba(255,140,66,0.15))' : 'rgba(255,255,255,0.04)',
                      color: filter === s ? '#ff6b47' : '#6b7280',
                      border: filter === s ? '1px solid rgba(255,77,77,0.3)' : '1px solid transparent',
                    }}
                  >
                    {s === 'tümü' ? 'Tümü' : s.charAt(0).toUpperCase() + s.slice(1)}
                  </button>
                ))}
              </div>
              <span className="ml-auto text-xs" style={{ color: '#6b7280' }}>{filtered.length} uzman</span>
            </div>

            {/* Table */}
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                    {['Uzman', 'Durum', 'Günlük Kazanç', 'İş', 'Puan', 'Son Aktiflik'].map((h) => (
                      <th key={h} className="text-left px-4 py-2.5 text-xs font-semibold uppercase tracking-wider" style={{ color: '#6b7280' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((e, idx) => (
                    <tr
                      key={e.id}
                      className="transition-all duration-200 cursor-default"
                      style={{ borderBottom: '1px solid rgba(255,255,255,0.04)', background: idx % 2 === 0 ? 'rgba(255,255,255,0.01)' : 'transparent' }}
                      onMouseEnter={(el) => { (el.currentTarget as HTMLTableRowElement).style.background = 'rgba(255,77,77,0.05)'; }}
                      onMouseLeave={(el) => { (el.currentTarget as HTMLTableRowElement).style.background = idx % 2 === 0 ? 'rgba(255,255,255,0.01)' : 'transparent'; }}
                    >
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0" style={{ background: 'linear-gradient(135deg, #ff4d4d, #ff8c42)' }}>{e.isim[0]}</div>
                          <div>
                            <div className="text-white font-medium text-sm">{e.isim}</div>
                            <div className="text-xs" style={{ color: '#6b7280' }}>{e.sehir} · {e.uzmanlik}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="flex items-center gap-1.5 text-xs font-medium px-2 py-1 rounded-full w-fit whitespace-nowrap" style={{ background: statusStyle[e.durum]?.bg, color: statusStyle[e.durum]?.color }}>
                          <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: statusStyle[e.durum]?.dot, boxShadow: `0 0 5px ${statusStyle[e.durum]?.dot}` }}></span>
                          {e.durum.charAt(0).toUpperCase() + e.durum.slice(1)}
                        </span>
                      </td>
                      <td className="px-4 py-3 font-medium" style={{ color: e.gunlukKazanc > 0 ? '#10b981' : '#4b5563', fontFamily: 'JetBrains Mono, monospace', fontSize: '13px' }}>
                        {e.gunlukKazanc > 0 ? `${e.gunlukKazanc.toLocaleString('tr-TR')} ₺` : '—'}
                      </td>
                      <td className="px-4 py-3 text-white font-medium">{e.isCount}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1">
                          <i className="ri-star-fill text-xs" style={{ color: '#ff8c42' }}></i>
                          <span className="text-white font-medium text-sm">{e.puan}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-xs" style={{ color: '#6b7280' }}>{e.sonAktif}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Side stats */}
        <div className="space-y-3">
          {/* Status summary */}
          <GlassCard title="Durum Özeti">
            <div className="space-y-3">
              {[
                { label: 'Online', count: onlineCount, color: '#10b981', pct: Math.round(onlineCount / activeExperts.length * 100) },
                { label: 'Meşgul', count: busyCount, color: '#f59e0b', pct: Math.round(busyCount / activeExperts.length * 100) },
                { label: 'Offline', count: offlineCount, color: '#6b7280', pct: Math.round(offlineCount / activeExperts.length * 100) },
              ].map((s) => (
                <div key={s.label}>
                  <div className="flex justify-between text-xs mb-1">
                    <span className="flex items-center gap-1.5" style={{ color: '#d1d5db' }}>
                      <span className="w-2 h-2 rounded-full" style={{ background: s.color }}></span>
                      {s.label}
                    </span>
                    <span className="font-medium" style={{ color: s.color }}>{s.count} uzman</span>
                  </div>
                  <div className="h-1.5 rounded-full" style={{ background: 'rgba(255,255,255,0.08)' }}>
                    <div className="h-1.5 rounded-full transition-all duration-700" style={{ width: `${s.pct}%`, background: s.color }} />
                  </div>
                </div>
              ))}
            </div>
          </GlassCard>

          {/* Top performers */}
          <GlassCard title="Bugün En Çok Kazanan">
            <div className="space-y-2">
              {activeExperts.filter((e) => e.gunlukKazanc > 0).sort((a, b) => b.gunlukKazanc - a.gunlukKazanc).slice(0, 5).map((e, i) => (
                <div key={e.id} className="flex items-center gap-2.5 p-2 rounded-lg" style={{ background: 'rgba(255,255,255,0.03)' }}>
                  <div className="w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold text-white flex-shrink-0" style={{ background: i === 0 ? 'linear-gradient(135deg,#ffd700,#ffaa00)' : 'linear-gradient(135deg,#ff4d4d,#ff8c42)', fontSize: '10px' }}>{i + 1}</div>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-medium text-white truncate">{e.isim}</div>
                    <div className="text-xs" style={{ color: '#6b7280', fontSize: '10px' }}>{e.isCount} iş</div>
                  </div>
                  <div className="text-xs font-bold whitespace-nowrap" style={{ color: '#10b981', fontFamily: 'JetBrains Mono, monospace' }}>{e.gunlukKazanc.toLocaleString('tr-TR')} ₺</div>
                </div>
              ))}
            </div>
          </GlassCard>

          {/* Performance trend */}
          <GlassCard title="Performans Trendi" subtitle="Top uzman, 6 ay">
            <ResponsiveContainer width="100%" height={140}>
              <LineChart data={performanceTrend}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                <XAxis dataKey="ay" tick={{ fill: '#6b7280', fontSize: 10 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fill: '#6b7280', fontSize: 10 }} axisLine={false} tickLine={false} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
                <Tooltip contentStyle={{ background: '#1a1a2e', border: '1px solid rgba(255,77,77,0.3)', borderRadius: '8px', fontSize: '11px' }} formatter={(v: number) => [`${v.toLocaleString('tr-TR')} ₺`]} />
                <Line type="monotone" dataKey="kazanc" name="Kazanç" stroke="#ff4d4d" strokeWidth={2} dot={{ r: 3, fill: '#ff4d4d' }} />
              </LineChart>
            </ResponsiveContainer>
          </GlassCard>
        </div>
      </div>
    </div>
  );
};

export default ExpertsPage;
